/**
 * ChatToolUI
 *
 * Shared conversational UI for chat-style tools (On the Record,
 * Platform Guide, Document Q&A, Deep Research Brief).
 * Streams tokens via useToolExecution, renders citations inline.
 */
import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Send,
  StopCircle,
  ExternalLink,
  RotateCcw,
  Copy,
  MessageCircleQuestion,
  X,
  Paperclip,
  Loader2,
} from 'lucide-react';

import { useToolExecution } from '@/hooks/useToolExecution';
import { apiFetch } from '@/lib/apiFetch';
import { MessageActions } from './MessageActions';
import { useUser } from '@/hooks/useUser';
import type { Tool } from '@/types/database';
import { DocumentViewerModal } from './DocumentViewerModal';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  citations?: Citation[];
  /** Follow-up questions generated for this assistant turn */
  followUpQuestions?: string[];
  /** tool_runs.id for this assistant response — used for feedback */
  toolRunId?: string;
}

interface Citation {
  chunkId: string;
  artifactUrl: string;
  /** Populated by dispatcher: DB artifact id — used to fetch a signed archive URL */
  artifactId?: string;
  /** Populated by dispatcher: human-readable artifact title */
  artifactTitle?: string;
  /** Populated by dispatcher: exact source page (from page-backfill) */
  pageNumber?: number;
  text: string;
}

interface AIModel {
  model_id: string;
  display_name: string;
}

interface ChatToolUIProps {
  tool: Tool;
  /** IDs of artifacts to scope retrieval to (Document Q&A) */
  artifactIds?: string[];
  /** Show model picker */
  showModelPicker?: boolean;
  placeholder?: string;
  /** Workspace ID to scope the session to */
  workspaceId?: string;
  /** Pre-fill the input with a question */
  prefillQuestion?: string;
  /**
   * When set, immediately sends this text as a user message.
   * Wrap in an object so the same string can be re-sent multiple times.
   */
  triggerQuestion?: { text: string; seq: number };
  /**
   * Called after each assistant response with the LLM-generated follow-up
   * questions extracted from the response.  Empty array when none present.
   */
  onFollowUpQuestions?: (questions: string[]) => void;
  /**
   * Optional file upload handler. When provided, a paperclip button appears
   * in the input row. The caller owns the upload logic and session tracking.
   */
  onFileUpload?: (file: File) => Promise<void>;
  /** Whether a file upload is in progress (shows spinner on paperclip) */
  uploading?: boolean;
  /** Accept string for the hidden file input, e.g. ".pdf,.docx" */
  uploadAccept?: string;
  /** Extra content to render below the input row (e.g. upload error banner) */
  inputAddon?: React.ReactNode;
  /** Extra CSS class applied to the outermost container div */
  className?: string;
}

export const ChatToolUI: React.FC<ChatToolUIProps> = ({
  tool,
  artifactIds,
  showModelPicker = false,
  placeholder = 'Ask a question…',
  workspaceId,
  prefillQuestion,
  triggerQuestion,
  onFollowUpQuestions,
  onFileUpload,
  uploading = false,
  uploadAccept = '.pdf,.doc,.docx,.xlsx,.xls,.csv,.txt,.md',
  inputAddon,
  className,
}) => {
  const { user } = useUser();
  const isAdmin = user?.role === 'SYSTEM_ADMIN' || user?.role === 'ORG_OWNER';

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState(prefillQuestion ?? '');
  const [models, setModels] = useState<AIModel[]>([]);
  const [selectedModel, setSelectedModel] = useState<string>('');
  /** Index of the assistant message whose follow-up modal is open */
  const [followUpModalIdx, setFollowUpModalIdx] = useState<number | null>(null);
  /** Citation currently open in the document viewer modal (PDF preview + download) */
  const [viewerCitation, setViewerCitation] = useState<Citation | null>(null);
  /** Index of copied message (for showing ✓ briefly) */
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  /**
   * Per-response feedback state: Map<toolRunId, 'up'|'down'>
   * Persisted across renders; restored from DB on mount / when new run IDs appear.
   */
  const [feedbackVotes, setFeedbackVotes] = useState<
    Map<string, 'up' | 'down'>
  >(new Map());
  /** One-tap reason chips for the standardized feedback widget. */
  const [chatReasons, setChatReasons] = useState<{
    up: { code: string; label: string }[];
    down: { code: string; label: string }[];
  }>({ up: [], down: [] });

  useEffect(() => {
    apiFetch('/api/feedback/reasons')
      .then((r) => r.json())
      .then(
        (d: {
          data?: Array<{ code: string; label: string; polarity: string }>;
        }) => {
          setChatReasons({
            up: (d?.data ?? []).filter((r) => r.polarity === 'up'),
            down: (d?.data ?? []).filter((r) => r.polarity === 'down'),
          });
        }
      )
      .catch(() => {
        /* non-critical */
      });
  }, []);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const promptInputRef = useRef<HTMLTextAreaElement>(null);

  // Close follow-up modal on ESC
  useEffect(() => {
    if (followUpModalIdx === null) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFollowUpModalIdx(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [followUpModalIdx]);
  const lastAssistantMessageRef = useRef<HTMLDivElement>(null);
  const messageCountRef = useRef(0);

  const {
    executing,
    streamingOutput,
    result,
    error,
    setError,
    executeTool,
    cancelExecution,
    resetExecution,
  } = useToolExecution();

  // Load models for picker
  useEffect(() => {
    if (!showModelPicker) return;
    apiFetch('/api/ai/models')
      .then((r) => r.json())
      .then((d) => {
        const list: AIModel[] = d?.data ?? d ?? [];
        setModels(list.filter((m) => m.model_id));
        if (list.length > 0 && !selectedModel) {
          setSelectedModel(list[0].model_id);
        }
      })
      .catch(() => {});
  }, [showModelPicker, selectedModel]);

  // Focus the prompt box on mount so the user can start typing immediately.
  // autoFocus covers the element render; the deferred pass covers hydration
  // timing where the window isn't interactive yet when the effect fires.
  useEffect(() => {
    promptInputRef.current?.focus();
    const t = setTimeout(() => promptInputRef.current?.focus(), 120);
    return () => clearTimeout(t);
  }, []);

  // Append streaming assistant turn once complete
  useEffect(() => {
    if (!executing && (streamingOutput || result)) {
      const content = streamingOutput || extractText(result);
      const blockError = content ? '' : extractError(result);
      if (content) {
        const citations = extractCitations(result);
        const followUps = extractFollowUpQuestions(result);
        const toolRunId = extractRunId(result);
        setMessages((prev) => {
          // Avoid appending duplicate if already appended
          const last = prev[prev.length - 1];
          if (last?.role === 'assistant' && last.content === content)
            return prev;
          // Replace streaming placeholder if present
          if (last?.role === 'assistant' && last.content === '…') {
            return [
              ...prev.slice(0, -1),
              {
                role: 'assistant',
                content,
                citations,
                followUpQuestions: followUps,
                toolRunId,
              },
            ];
          }
          return [
            ...prev,
            {
              role: 'assistant',
              content,
              citations,
              followUpQuestions: followUps,
              toolRunId,
            },
          ];
        });
        // Notify parent with live follow-up questions from this response
        onFollowUpQuestions?.(followUps);
      } else if (blockError) {
        // An adapter/engine error block that produced no visible text —
        // surface it after reset instead of rendering a blank bubble.
        resetExecution();
        setError(new Error(blockError));
        return;
      }
      resetExecution();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [executing]);

  // Auto-scroll: scroll to top of new assistant message when it appears
  useEffect(() => {
    const currentMessageCount = messages.length;
    const previousMessageCount = messageCountRef.current;

    // If a new message was added and it's an assistant message
    if (currentMessageCount > previousMessageCount) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.role === 'assistant') {
        // Scroll to the top of the new assistant message (ChatGPT/Claude style)
        lastAssistantMessageRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start', // Align to top of viewport
        });
      }
    }

    // Update the count
    messageCountRef.current = currentMessageCount;
  }, [messages]);

  // Fire when a suggested question is clicked from the context panel
  useEffect(() => {
    if (!triggerQuestion?.text) return;
    setInput('');
    void sendText(triggerQuestion.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerQuestion?.seq]);

  // Restore feedback vote state whenever new assistant messages appear
  useEffect(() => {
    const runIds = messages
      .filter((m) => m.role === 'assistant' && m.toolRunId)
      .map((m) => m.toolRunId as string)
      // Only fetch IDs we haven't already fetched
      .filter((id) => !feedbackVotes.has(id));

    if (runIds.length === 0) return;

    apiFetch(`/api/feedback/mine?tool_run_ids=${runIds.join(',')}`)
      .then((r) => r.json())
      .then((d) => {
        const rows: { tool_run_id: string; rating: 'up' | 'down' }[] =
          d?.data ?? [];
        if (rows.length === 0) return;
        setFeedbackVotes((prev) => {
          const next = new Map(prev);
          rows.forEach((r) => next.set(r.tool_run_id, r.rating));
          return next;
        });
      })
      .catch(() => {
        /* non-critical */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  /** POST /api/feedback and update local voted state */
  const submitFeedback = async (
    toolRunId: string,
    rating: 'up' | 'down',
    reasonCode?: string,
    comment?: string
  ) => {
    try {
      const res = await apiFetch('/api/feedback', {
        method: 'POST',
        body: JSON.stringify({
          tool_run_id: toolRunId,
          rating,
          ...(reasonCode ? { reason_code: reasonCode } : {}),
          ...(comment ? { comment } : {}),
          ...(workspaceId ? { workspace_id: workspaceId } : {}),
        }),
      });
      const j = (await res.json().catch(() => null)) as {
        action_triggered?: 'reprocess' | 'admin_review' | null;
      } | null;
      setFeedbackVotes((prev) => {
        const next = new Map(prev);
        next.set(toolRunId, rating);
        return next;
      });
      return {
        actionTriggered: (j?.action_triggered ?? null) as
          | 'reprocess'
          | 'admin_review'
          | null,
      };
    } catch {
      return { actionTriggered: null };
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || executing) return;
    await sendText(text);
    setInput('');
  };

  const sendText = async (text: string) => {
    if (!text || executing) return;
    const userMsg: Message = { role: 'user', content: text };
    const allMessages = [...messages, userMsg];
    setMessages([...allMessages, { role: 'assistant', content: '…' }]);

    resetExecution();

    try {
      await executeTool({
        toolId: tool.id,
        ...(workspaceId ? { workspaceId } : {}),
        input: {
          messages: allMessages.map((m) => ({
            role: m.role,
            content: m.content,
          })),
          ...(artifactIds ? { artifactIds } : {}),
          ...(selectedModel ? { params: { model_id: selectedModel } } : {}),
        },
      });
    } catch {
      /* non-critical */
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleReset = () => {
    setMessages([]);
    resetExecution();
    onFollowUpQuestions?.([]);
  };

  return (
    <div
      className={`flex flex-col h-[calc(100vh-220px)] min-h-[480px] ${className ?? ''}`}
    >
      {/* Toolbar */}
      {(showModelPicker || messages.length > 0) && (
        <div className="flex items-center justify-between mb-3 gap-3">
          {showModelPicker && models.length > 0 && (
            <Select value={selectedModel} onValueChange={setSelectedModel}>
              <SelectTrigger className="w-56 h-8 text-sm">
                <SelectValue placeholder="Select model" />
              </SelectTrigger>
              <SelectContent>
                {models.map((m) => (
                  <SelectItem key={m.model_id} value={m.model_id}>
                    {m.display_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto text-gray-900"
              onClick={handleReset}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              New conversation
            </Button>
          )}
        </div>
      )}

      {/* Message list */}
      <div className="flex-1 overflow-y-auto rounded-xl border border-gray-300 bg-white p-4 space-y-5">
        {messages.length === 0 && !executing && (
          <div className="flex h-full items-center justify-center text-center text-sm text-gray-900">
            <div>
              <p className="font-medium text-gray-900 mb-1">{tool.name}</p>
              <p>{tool.description ?? 'Send a message to get started.'}</p>
            </div>
          </div>
        )}

        {messages.map((msg, i) => {
          // Each assistant message carries its own follow-ups
          const followUps =
            msg.role === 'assistant' ? (msg.followUpQuestions ?? []) : [];
          const isModalOpen = followUpModalIdx === i;

          return (
            <div key={i}>
              <div
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  ref={
                    msg.role === 'assistant' && i === messages.length - 1
                      ? lastAssistantMessageRef
                      : null
                  }
                  className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white text-[14px] font-medium'
                      : 'bg-gray-50 border border-gray-300 text-gray-950'
                  }`}
                >
                  {/* Streaming placeholder */}
                  {msg.content === '…' && executing ? (
                    <span className="flex items-center gap-2">
                      {/* Rainbow thinking dots */}
                      <span className="flex items-center gap-1">
                        {[0, 1, 2].map((dot) => (
                          <span
                            key={dot}
                            className="inline-block h-2 w-2 rounded-full animate-pride-cycle"
                            style={{ animationDelay: `${dot * 0.22}s` }}
                          />
                        ))}
                      </span>
                      {streamingOutput ? (
                        <span className="text-gray-700 text-sm">
                          {streamingOutput}
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-gray-500">
                          Working…
                        </span>
                      )}
                    </span>
                  ) : msg.role === 'user' ? (
                    <span className="whitespace-pre-wrap">{msg.content}</span>
                  ) : (
                    <div
                      className="prose prose-base max-w-none
                        text-gray-950
                        prose-p:my-2 prose-p:leading-7 prose-p:text-gray-950
                        prose-headings:font-bold prose-headings:text-gray-950 prose-headings:mt-4 prose-headings:mb-2
                        prose-h1:text-lg prose-h2:text-base prose-h3:text-sm
                        prose-ul:my-2 prose-ul:pl-5 prose-ol:my-2 prose-ol:pl-5
                        prose-li:my-1 prose-li:text-gray-950
                        prose-strong:font-bold prose-strong:text-gray-950
                        prose-em:text-gray-800
                        prose-code:bg-gray-100 prose-code:text-gray-900 prose-code:rounded prose-code:px-1.5 prose-code:py-0.5 prose-code:text-[13px] prose-code:font-mono prose-code:border prose-code:border-gray-200
                        prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-xl prose-pre:p-4 prose-pre:overflow-x-auto prose-pre:text-[13px]
                        prose-blockquote:border-l-4 prose-blockquote:border-blue-400 prose-blockquote:bg-blue-50 prose-blockquote:pl-4 prose-blockquote:py-1 prose-blockquote:rounded-r prose-blockquote:text-gray-800 prose-blockquote:not-italic
                        prose-table:text-sm prose-th:bg-gray-100 prose-th:text-gray-900 prose-th:font-bold prose-th:px-3 prose-th:py-2 prose-td:border prose-td:border-gray-300 prose-td:px-3 prose-td:py-2 prose-td:text-gray-900
                        prose-a:text-blue-700 prose-a:font-medium prose-a:underline hover:prose-a:text-blue-900
                        prose-hr:border-gray-300"
                    >
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {parseLlmSources(msg.content).mainContent}
                      </ReactMarkdown>
                    </div>
                  )}

                  {/* Citations */}
                  {(() => {
                    if (msg.role !== 'assistant') return null;
                    const { noLinkSources } = parseLlmSources(msg.content);
                    const seen = new Set<string>();
                    const linkedCitations = (msg.citations ?? []).filter(
                      (c) => {
                        if (!c.artifactUrl || seen.has(c.artifactUrl))
                          return false;
                        seen.add(c.artifactUrl);
                        return true;
                      }
                    );
                    if (!linkedCitations.length && !noLinkSources.length)
                      return null;
                    return (
                      <div className="mt-3 border-t border-gray-200 pt-3">
                        <Accordion type="multiple" className="w-full">
                          {linkedCitations.length > 0 && (
                            <AccordionItem value="linked-sources">
                              <AccordionTrigger className="text-xs font-bold uppercase tracking-wide text-gray-700 hover:no-underline">
                                <span>
                                  Sources (W/ Document Links){' '}
                                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                                    {linkedCitations.length}
                                  </span>
                                </span>
                              </AccordionTrigger>
                              <AccordionContent>
                                <ul className="space-y-2">
                                  {linkedCitations.map((c, ci) => (
                                    <li key={ci}>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          c.artifactId
                                            ? setViewerCitation(c)
                                            : window.open(
                                                c.artifactUrl,
                                                '_blank',
                                                'noopener,noreferrer'
                                              )
                                        }
                                        className="w-full flex items-start gap-2 rounded-lg px-2 py-1.5 -mx-2 hover:bg-blue-50 transition-colors group text-left"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-blue-400 group-hover:text-blue-600" />
                                        <span className="flex flex-col min-w-0">
                                          <span className="text-sm font-semibold text-gray-900 leading-snug group-hover:text-blue-800">
                                            {citationLabel(c)}
                                          </span>
                                          <span className="text-xs text-gray-900 truncate mt-0.5">
                                            {citationUrlLabel(c.artifactUrl)}
                                          </span>
                                          {typeof c.pageNumber === 'number' && (
                                            <span className="mt-1 inline-flex w-fit items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-semibold text-blue-700">
                                              {citationLabel(c)} — p.
                                              {c.pageNumber}
                                            </span>
                                          )}
                                        </span>
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              </AccordionContent>
                            </AccordionItem>
                          )}

                          {noLinkSources.length > 0 && (
                            <AccordionItem value="no-link-sources">
                              <AccordionTrigger className="text-xs font-bold uppercase tracking-wide text-gray-700 hover:no-underline">
                                <span>
                                  Sources (No Links Found){' '}
                                  <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                                    {noLinkSources.length}
                                  </span>
                                </span>
                              </AccordionTrigger>
                              <AccordionContent>
                                <ul className="space-y-1.5">
                                  {noLinkSources.map((title, si) => (
                                    <li
                                      key={si}
                                      className="flex items-start gap-2 px-2 py-1"
                                    >
                                      <span className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 flex items-center justify-center">
                                        <span className="h-1.5 w-1.5 rounded-full bg-gray-500" />
                                      </span>
                                      <span className="text-sm font-medium text-gray-800 leading-snug">
                                        {title}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </AccordionContent>
                            </AccordionItem>
                          )}
                        </Accordion>
                      </div>
                    );
                  })()}

                  {/* ── Action bar (assistant only, not while streaming) ── */}
                  {msg.role === 'assistant' && msg.content !== '…' && (
                    <div className="mt-3 pt-2 border-t border-gray-200">
                      <div className="flex items-center gap-0.5">
                        {/* Copy */}
                        <button
                          type="button"
                          title="Copy response"
                          onClick={() => {
                            void navigator.clipboard.writeText(
                              parseLlmSources(msg.content).mainContent
                            );
                            setCopiedIdx(i);
                            setTimeout(
                              () => setCopiedIdx((p) => (p === i ? null : p)),
                              2000
                            );
                          }}
                          className="relative group flex items-center justify-center h-7 w-7 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                        >
                          {copiedIdx === i ? (
                            <span className="text-[11px] font-bold text-green-600">
                              ✓
                            </span>
                          ) : (
                            <Copy className="h-3.5 w-3.5" />
                          )}
                          <span
                            className="pointer-events-none absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2
                                           whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[11px] text-white
                                           opacity-0 group-hover:opacity-100 transition-opacity z-10"
                          >
                            Copy response
                          </span>
                        </button>

                        {/* Follow-up questions — only when available */}
                        {followUps.length > 0 && (
                          <button
                            type="button"
                            title="Follow-up questions"
                            onClick={() =>
                              setFollowUpModalIdx(isModalOpen ? null : i)
                            }
                            className={`relative group flex items-center justify-center h-7 w-7 rounded-md transition-colors
                                        ${
                                          isModalOpen
                                            ? 'text-blue-600 bg-blue-100'
                                            : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                                        }`}
                          >
                            <MessageCircleQuestion className="h-3.5 w-3.5" />
                            <span
                              className="pointer-events-none absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2
                                             whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-[11px] text-white
                                             opacity-0 group-hover:opacity-100 transition-opacity z-10"
                            >
                              Follow-up questions
                            </span>
                          </button>
                        )}
                      </div>

                      {/* Standardized feedback — its own row AFTER the response */}
                      {msg.toolRunId && (
                        <div className="mt-1.5">
                          <MessageActions
                            toolRunId={msg.toolRunId}
                            initialRating={feedbackVotes.get(msg.toolRunId)}
                            reasonsDown={chatReasons.down}
                            reasonsUp={chatReasons.up}
                            onFeedback={({ rating, reasonCode, comment }) =>
                              submitFeedback(
                                msg.toolRunId as string,
                                rating,
                                reasonCode,
                                comment
                              )
                            }
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── No inline modal here — modal is rendered at component root ── */}
            </div>
          );
        })}

        {error && (
          <div className="flex justify-start">
            <div className="rounded-2xl bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700 max-w-[80%]">
              {error.message}
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Follow-up questions modal ─────────────────────────────────── */}
      {followUpModalIdx !== null &&
        (() => {
          const modalMsg = messages[followUpModalIdx];
          const modalFollowUps = modalMsg?.followUpQuestions ?? [];
          if (modalFollowUps.length === 0) return null;

          return (
            /* Backdrop — click to close */
            <div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setFollowUpModalIdx(null);
              }}
            >
              <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                  <div>
                    <p className="text-sm font-bold text-gray-900">
                      Follow-up Questions
                    </p>
                    <p className="text-xs text-gray-900 mt-0.5">
                      Click a question to continue this conversation
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFollowUpModalIdx(null)}
                    aria-label="Close"
                    className="flex items-center justify-center h-8 w-8 rounded-lg text-gray-700
                             hover:text-gray-700 hover:bg-gray-100 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Question list */}
                <ul className="p-3 space-y-1 overflow-y-auto max-h-[60vh]">
                  {modalFollowUps.map((q, qi) => (
                    <li key={qi}>
                      <button
                        type="button"
                        onClick={() => {
                          setFollowUpModalIdx(null);
                          void sendText(q);
                        }}
                        className="w-full text-left flex items-start gap-3 rounded-xl px-4 py-3
                                 text-sm text-gray-800 hover:bg-blue-50 hover:text-blue-900
                                 transition-colors group border border-transparent hover:border-blue-100"
                      >
                        <span className="text-blue-300 group-hover:text-blue-500 flex-shrink-0 mt-0.5 text-base transition-colors">
                          ✦
                        </span>
                        <span className="leading-snug">{q}</span>
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Footer */}
                <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setFollowUpModalIdx(null)}
                    className="px-4 py-2 rounded-lg text-sm font-medium text-gray-900
                             bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* ── Document viewer modal (archived copy — PDF preview + download) ── */}
      {viewerCitation && viewerCitation.artifactId && (
        <DocumentViewerModal
          artifactId={viewerCitation.artifactId}
          sourceUrl={viewerCitation.artifactUrl}
          title={citationLabel(viewerCitation)}
          kind="pdf"
          onClose={() => setViewerCitation(null)}
        />
      )}

      {/* Input row */}
      <div className="mt-3 flex items-end gap-2">
        <Textarea
          ref={promptInputRef}
          autoFocus
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={2}
          className="flex-1 resize-none text-sm bg-white text-gray-900 placeholder:text-gray-400 border-gray-400"
          disabled={executing}
        />
        {executing ? (
          <Button
            size="icon"
            variant="outline"
            className="h-10 w-10 flex-shrink-0 text-red-600 border-red-200"
            onClick={cancelExecution}
          >
            <StopCircle className="h-4 w-4" />
          </Button>
        ) : (
          <>
            {/* Paperclip — only when caller provides an upload handler */}
            {onFileUpload && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept={uploadAccept}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) void onFileUpload(file);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                />
                <Button
                  variant="default"
                  size="icon"
                  className="h-10 w-10 flex-shrink-0 bg-gray-800 border-gray-800 hover:bg-gray-700 hover:border-gray-700 text-white"
                  title="Upload a document"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Paperclip className="h-4 w-4" />
                  )}
                </Button>
              </>
            )}
            <Button
              size="icon"
              className="h-10 w-10 flex-shrink-0"
              onClick={() => void handleSend()}
              disabled={!input.trim()}
            >
              <Send className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>

      {/* Slot for caller-provided extras (e.g. upload error banner) */}
      {inputAddon}

      {/* Engine badge — admins only */}
      {isAdmin && (
        <div className="mt-2 flex justify-end">
          <Badge variant="outline" className="text-xs text-gray-700">
            {(tool as Tool & { engine?: string }).engine ?? tool.type}
          </Badge>
        </div>
      )}
    </div>
  );
};

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Parse the LLM response to extract:
 *   - mainContent: the answer text with any LLM-written "Sources" block removed
 *   - noLinkSources: source titles the LLM listed that have no real URL
 *
 * Handles multiple formats the LLM uses:
 *   ## Sources\n- Title (not available...)\n...
 *   Sources\n- Title...\n
 *   City Council Minutes, Jan 12 - not available via URL...
 */
function parseLlmSources(content: string): {
  mainContent: string;
  noLinkSources: string[];
} {
  // Match heading-style sources block: ## Sources / **Sources** / SOURCES
  const headingMatch = content.match(
    /\n{0,2}(?:#{1,3}\s*|(?:\*\*)?)?(?:sources?|citations?|references?)(?:\*\*)?\s*\n([\s\S]*)$/i
  );

  if (!headingMatch) {
    return { mainContent: content.trim(), noLinkSources: [] };
  }

  const mainContent = content
    .slice(0, content.length - headingMatch[0].length)
    .trim();
  const block = headingMatch[1] ?? '';

  // Parse individual entries — bullet/numbered list or plain sentences
  const lines = block
    .split('\n')
    .map((l) => l.replace(/^[-•*]\s*|\d+\.\s*/, '').trim())
    .filter((l) => l.length > 10);

  // Only keep entries that look "not available" / no real URL
  // If the line contains a real URL we skip it (the backend citation already covers it)
  const noLinkSources = lines.filter(
    (l) => !l.match(/https?:\/\//) && l.length > 0
  );

  return { mainContent, noLinkSources };
}

/**
 * Build a human-readable label for a citation.
 * Priority: artifactTitle (populated by dispatcher) → text field → URL filename.
 * Never shows raw chunk text or hallucinated page numbers.
 */
function citationLabel(c: Citation): string {
  // Best: dispatcher-enriched title
  if (c.artifactTitle && c.artifactTitle.trim()) {
    return c.artifactTitle.trim();
  }
  // Fallback: short text if it looks like a title (< 120 chars, no newlines)
  if (
    c.text &&
    c.text.trim() &&
    c.text.length < 120 &&
    !c.text.includes('\n')
  ) {
    return c.text.trim();
  }
  // Last resort: derive from URL filename
  return citationUrlLabel(c.artifactUrl) || c.artifactUrl;
}

/**
 * Format a URL into a short readable string for secondary display.
 * e.g. "cityoflacrescent-mn.gov · 10-13-2025-PDF-1.pdf"
 */
function citationUrlLabel(artifactUrl: string): string {
  try {
    const url = new URL(artifactUrl);
    const host = url.hostname.replace(/^www\./, '');
    const filename = url.pathname.split('/').pop() ?? '';
    if (filename) return `${host} · ${decodeURIComponent(filename)}`;
    return host;
  } catch {
    return artifactUrl;
  }
}

function extractText(result: unknown): string {
  if (!result) return '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = result as any;

  // Handle wrapped response: { success: true, data: { blocks: [...] } }
  const data = r?.data ?? r;

  if (data?.blocks) {
    return (
      data.blocks
        // 'text' blocks render directly; 'json' blocks (raw JSON strings from
        // json_object mode) fall back to their raw content so a JSON-wrapped
        // response is never dropped as blank. 'error' blocks are excluded here —
        // see extractError.
        .filter(
          (b: { type: string; content?: string }) =>
            b.type === 'text' ||
            (b.type === 'json' && typeof b.content === 'string')
        )
        .map((b: { type: string; content: string }) =>
          typeof b.content === 'string' ? b.content : JSON.stringify(b.content)
        )
        .join('\n')
    );
  }
  if (typeof data?.output === 'string') return data.output;
  return '';
}

function extractError(result: unknown): string {
  if (!result) return '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = result as any;
  const data = r?.data ?? r;
  if (data?.blocks) {
    const errBlock = data.blocks.find(
      (b: { type: string; content?: string }) => b.type === 'error'
    );
    if (errBlock && typeof errBlock.content === 'string' && errBlock.content) {
      return errBlock.content;
    }
  }
  if (typeof data?.error === 'string') return data.error;
  return '';
}

function extractFollowUpQuestions(result: unknown): string[] {
  if (!result) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = result as any;
  const data = r?.data ?? r;
  if (Array.isArray(data?.followUpQuestions)) {
    return data.followUpQuestions.filter(
      (q: unknown) => typeof q === 'string' && q.trim().length > 0
    );
  }
  return [];
}

function extractCitations(result: unknown): Citation[] {
  if (!result) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = result as any;

  // Handle wrapped response: { success: true, data: { citations: [...] } }
  const data = r?.data ?? r;

  if (Array.isArray(data?.citations)) {
    return data.citations.map((c: Citation) => ({
      chunkId: c.chunkId ?? '',
      artifactId: c.artifactId ?? undefined,
      artifactUrl: c.artifactUrl ?? '',
      artifactTitle: c.artifactTitle ?? undefined,
      pageNumber: c.pageNumber,
      text: c.text ?? '',
    }));
  }
  return [];
}

/**
 * Extract the tool_runs.id (runId) from the dispatch result envelope.
 * The dispatcher returns { ...result, runId } wrapped as { success, data: { ..., runId } }.
 */
function extractRunId(result: unknown): string | undefined {
  if (!result) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = result as any;
  const data = r?.data ?? r;
  return typeof data?.runId === 'string' ? data.runId : undefined;
}
